import { useState, useEffect, useMemo } from "react";
import { AssetService } from "@/services/asset-service";
import type { PerformanceData, Asset } from "@/schemas";

export interface UsePortfolioAssetsReturn {
  assetsMap: Map<string, Asset>;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch and cache assets for portfolio allocation charts.
 * Extracts asset IDs from performance data and fetches them in bulk.
 *
 * @param performanceData - Performance data containing asset information
 * @returns Map of asset IDs to Asset objects, loading state, and error
 */
export function usePortfolioAssets(
  performanceData: PerformanceData | null
): UsePortfolioAssetsReturn {
  const [assetsMap, setAssetsMap] = useState<Map<string, Asset>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Extract asset IDs that need to be fetched (stocks, ETFs, and funds)
  const assetIds = useMemo(() => {
    if (!performanceData?.assets_performance) {
      return [];
    }

    return Object.entries(performanceData.assets_performance)
      .filter(([, assetPerf]) =>
        assetPerf.asset_type === "stock" ||
        assetPerf.asset_type === "etf" ||
        assetPerf.asset_type === "fund"
      )
      .map(([assetId]) => assetId);
  }, [performanceData]);

  useEffect(() => {
    const fetchAssets = async () => {
      // No assets to fetch
      if (assetIds.length === 0) {
        setAssetsMap(new Map());
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch all assets in a single bulk request
        const assets = await AssetService.getAssetsByIDs(assetIds);

        // Convert array to Map for O(1) lookups
        const map = new Map<string, Asset>();
        assets.forEach(asset => {
          map.set(asset.asset_id, asset);
        });

        setAssetsMap(map);
      } catch (err) {
        console.error("Failed to fetch portfolio assets:", err);
        setError(err instanceof Error ? err : new Error("Unknown error"));
        setAssetsMap(new Map());
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, [assetIds.join(",")]); // Only refetch if the list of IDs changes

  return { assetsMap, loading, error };
}
